import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Avatar,
  Chip
} from '@mui/material';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NearMeIcon from '@mui/icons-material/NearMe';
import './ChatBox.css';

const ChatBox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [direccion, setDireccion] = useState('');
  const [resultados, setResultados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const RADIO_BUSQUEDA = 30;

  const buscarLineas = async () => {
    if (!direccion.trim()) {
      setError('Por favor ingresa una dirección');
      return;
    }

    setLoading(true);
    setError(null);
    setResultados(null);
    
    try {
      
      const url = new URL('https://mapa.ne3publicidad.com.ar/api/api/buscarPorDireccion');
      url.searchParams.append('direccion', direccion.trim());
      url.searchParams.append('radio', RADIO_BUSQUEDA);
      
      console.log('Consultando URL:', url.toString());
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!response.ok) {
        let errorMessage = `Error ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.mensaje || errorData.error || errorMessage;
        } catch (e) {
          // No se pudo parsear JSON, usar texto plano
          errorMessage = await response.text() || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      console.log('Respuesta recibida:', data);
      
      // Adaptar la respuesta al nuevo formato del backend - CORREGIDO
      const resultadosAdaptados = {
        direccion_buscada: data.ubicacion_buscada?.direccion || direccion.trim(),
        coordenadas: {
          lat: data.ubicacion_buscada?.lat,
          lng: data.ubicacion_buscada?.lng
        },
        radio_busqueda_metros: data.radio_busqueda_metros || RADIO_BUSQUEDA,
        lineas_encontradas: data.total_lineas_encontradas || 0,
        // CORRECCIÓN: Usar data.detalle en lugar de data.lineas
        lineas: (data.detalle || data.lineas || []).map(item => {
          // Si item es un string (del array lineas)
          if (typeof item === 'string') {
            return {
              linea: item,
              color: '#1976d2', // Color por defecto
              distancia_metros: null,
              punto_mas_cercano: null
            };
          }
          // Si item es un objeto (del array detalle)
          return {
            linea: item.linea,
            color: item.color || '#1976d2',
            distancia_metros: item.distancia_metros,
            punto_mas_cercano: item.punto_mas_cercano || null
          };
        }),
        precision: data.precision || ""
      };
      
      setResultados(resultadosAdaptados);
    } catch (err) {
      console.error('Error completo en la búsqueda:', err);
      
      // Manejar errores específicos
      const errorMsg = err.message.toLowerCase();
      
      if (errorMsg.includes('geocodificación') || errorMsg.includes('400')) {
        setError('No se pudo encontrar esa dirección. Verifica que esté correctamente escrita.');
      } else if (errorMsg.includes('500') || errorMsg.includes('servidor')) {
        setError('Error del servidor. Por favor, intenta más tarde.');
      } else if (errorMsg.includes('network') || errorMsg.includes('failed to fetch') || errorMsg.includes('fetch')) {
        setError('Error de conexión. Verifica tu internet e intenta nuevamente.');
      } else if (errorMsg.includes('no se encontraron') || errorMsg.includes('0 líneas')) {
        setError('No se encontraron líneas en esa ubicación. Intenta con una dirección cercana a una parada conocida.');
      } else if (errorMsg.includes('cors')) {
        setError('Error de configuración del servidor. Contacta al administrador.');
      } else {
        setError(err.message || 'Error en la búsqueda. Intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    buscarLineas();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const resetChat = () => {
    setDireccion('');
    setResultados(null);
    setError(null);
  };

  // Función para determinar el color según la distancia
  const getDistanciaColor = (distancia) => {
    if (distancia <= 5) return '#4caf50'; // Verde
    if (distancia <= 15) return '#ff9800'; // Naranja
    return '#f44336'; // Rojo
  };

  return (
    <>
      {/* Botón flotante */}
      {!isOpen && (
        <IconButton
          className="chatbox-floating-button bus-button"
          onClick={() => setIsOpen(true)}
          color="primary"
          size="large"
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: '#1976d2',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1565c0'
            },
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          <DirectionsBusIcon fontSize="large" />
        </IconButton>
      )}

      {/* Ventana del chat */}
      {isOpen && (
        <Paper className="chatbox-window" elevation={3} sx={{
          position: 'fixed',
          bottom: 80,
          right: 20,
          width: 380,
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflow: 'hidden',
          zIndex: 1000
        }}>
          <Box className="chatbox-header bus-header" sx={{
            bgcolor: '#1976d2',
            color: 'white',
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar 
                sx={{ 
                  bgcolor: 'white', 
                  color: '#1976d2',
                  width: 32,
                  height: 32
                }}
              >
                <DirectionsBusIcon fontSize="small" />
              </Avatar>
              <Typography variant="h6" component="div">
                Buscador de Líneas
              </Typography>
            </Box>
            <IconButton onClick={() => { setIsOpen(false); resetChat(); }} size="small" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box className="chatbox-content" sx={{ p: 2, overflowY: 'auto', maxHeight: 'calc(80vh - 64px)' }}>
            {resultados ? (
              <Card variant="outlined" sx={{ borderRadius: '12px' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Resultados para:
                    </Typography>
                    
                  </Box>
                  
                  <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                    {resultados.direccion_buscada}
                  </Typography>
                  
                  {resultados.precision && (
                    <Chip 
                      icon={<NearMeIcon />}
                      label={resultados.precision}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  <Typography variant="body2" gutterBottom>
                    <strong>Líneas encontradas:</strong> {resultados.lineas_encontradas}
                  </Typography>
                  
                  {resultados.lineas_encontradas > 0 ? (
                    <List dense sx={{ maxHeight: 250, overflow: 'auto', mt: 1 }}>
                      {resultados.lineas.map((linea, index) => (
                        <ListItem 
                          key={index} 
                          sx={{ 
                            borderRadius: '8px',
                            mb: 1,
                            backgroundColor: 'rgba(25, 118, 210, 0.05)',
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box display="flex" alignItems="center">
                                  <Box component="span" sx={{ 
                                    display: 'inline-block',
                                    width: 12,
                                    height: 12,
                                    backgroundColor: linea.color || '#1976d2',
                                    borderRadius: '50%',
                                    mr: 1.5,
                                    border: '1px solid rgba(0,0,0,0.2)'
                                  }} />
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    Línea {linea.linea}
                                  </Typography>
                                </Box>
                               
                              </Box>
                            }
                            secondary={
                              linea.punto_mas_cercano ? (
                                <Typography variant="caption" color="textSecondary">
                                  Punto más cercano: {linea.punto_mas_cercano}
                                </Typography>
                              ) : null
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box textAlign="center" py={3}>
                      <LocationOnIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        No se encontraron líneas en un radio de {RADIO_BUSQUEDA} metros.
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Sugerencias:
                      </Typography>
                      <Box component="ul" sx={{ 
                        textAlign: 'left', 
                        pl: 2, 
                        fontSize: '0.8rem', 
                        color: 'text.secondary',
                        mt: 1
                      }}>
                        <li>Verifica que la dirección esté correctamente escrita</li>
                        <li>Intenta con una dirección más cercana a una esquina o parada</li>
                        <li>Prueba con nombres de calles principales</li>
                        
                      </Box>
                    </Box>
                  )}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetChat}
                    sx={{ mt: 2, borderRadius: '20px' }}
                    startIcon={<LocationOnIcon />}
                    fullWidth
                  >
                    Nueva Búsqueda
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <form onSubmit={handleSubmit}>
                <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                  Ingresa una dirección para buscar líneas de colectivo que pasen cerca:
                </Typography>
                
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Ej: Av. Corrientes 1234, Buenos Aires"
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    startAdornment: <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  sx={{ 
                    mb: 2, 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                    }
                  }}
                  disabled={loading}
                  autoFocus
                />
                
                <Box sx={{ 
                  mb: 2, 
                  p: 1.5, 
                  backgroundColor: 'rgba(25, 118, 210, 0.05)', 
                  borderRadius: '8px',
                  border: '1px solid rgba(25, 118, 210, 0.1)'
                }}>
                 
                </Box>
                
                {error && (
                  <Box sx={{ 
                    mb: 2, 
                    p: 1.5, 
                    backgroundColor: 'rgba(244, 67, 54, 0.08)', 
                    borderRadius: '8px',
                    border: '1px solid rgba(244, 67, 54, 0.2)'
                  }}>
                    <Typography 
                      color="error" 
                      variant="caption"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <CloseIcon fontSize="small" />
                      {error}
                    </Typography>
                  </Box>
                )}
                
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading || !direccion.trim()}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  sx={{ 
                    borderRadius: '20px',
                    py: 1,
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0'
                    }
                  }}
                >
                  {loading ? 'Buscando líneas...' : 'Buscar Líneas Cercanas'}
                </Button>
                
                <Typography variant="caption" color="textSecondary" sx={{ 
                  display: 'block', 
                  mt: 1.5, 
                  textAlign: 'center' 
                }}>
                  Sistema de búsqueda de líneas de colectivo por proximidad
                </Typography>
              </form>
            )}
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ChatBox;